import React, { useState, useMemo } from 'react';
import {
  X,
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  Calendar,
  Search,
  Building,
  ArrowRight,
  Filter,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { ProjectTask, getConcernedPersons, getProjectRoleAssignments, getDrawingTaskInfo, formatToDDMMYY } from './types.ts';
import { TEAM_MEMBERS } from './initialProjects.ts';

interface TeamTasksDeadlinesModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: ProjectTask[];
  onSelectProject?: (project: ProjectTask) => void;
  onFilterEngineerInTable?: (engineerName: string) => void;
}

export const TeamTasksDeadlinesModal: React.FC<TeamTasksDeadlinesModalProps> = ({
  isOpen,
  onClose,
  projects,
  onSelectProject,
  onFilterEngineerInTable
}) => {
  const [selectedMember, setSelectedMember] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'on_time' | 'due_today' | 'overdue' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Compute metrics per team member
  const memberData = useMemo(() => {
    return TEAM_MEMBERS.map(tm => {
      const tmLower = tm.name.toLowerCase();

      // Find all projects where this member is assigned in any core role
      const assigned = projects.filter(p => {
        const persons = getConcernedPersons(p);
        return persons.some(per => per.toLowerCase() === tmLower);
      });

      let onTimeCount = 0;
      let dueTodayCount = 0;
      let overdueCount = 0;
      let completedCount = 0;

      const taskList = assigned.map(p => {
        const info = getDrawingTaskInfo(p);
        const roles = getProjectRoleAssignments(p).filter(
          r => r.person.toLowerCase() === tmLower
        );
        const roleNames = roles.map(r => r.role).join(', ') || 'Incharge';

        if (info.status === 'completed') completedCount++;
        else if (info.status === 'overdue') overdueCount++;
        else if (info.status === 'due_today') dueTodayCount++;
        else if (info.status === 'on_time') onTimeCount++;

        return {
          project: p,
          taskInfo: info,
          assignedRoles: roleNames
        };
      });

      return {
        member: tm,
        total: assigned.length,
        onTimeCount,
        dueTodayCount,
        overdueCount,
        completedCount,
        taskList
      };
    });
  }, [projects]);

  // Overall counts across all projects
  const overallMetrics = useMemo(() => {
    let onTime = 0;
    let dueToday = 0;
    let overdue = 0;
    let completed = 0;

    projects.forEach(p => {
      const info = getDrawingTaskInfo(p);
      if (info.status === 'completed') completed++;
      else if (info.status === 'overdue') overdue++;
      else if (info.status === 'due_today') dueToday++;
      else if (info.status === 'on_time') onTime++;
    });

    return {
      total: projects.length,
      onTime,
      dueToday,
      overdue,
      completed
    };
  }, [projects]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-blue-950 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-300">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Team Member Tasks & Drawing Deadlines</h2>
              <p className="text-xs text-slate-300">
                Track which team member has which job and verify if tasks are on time, completed, or overdue
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Top Summary Metric Cards */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 grid grid-cols-2 sm:grid-cols-5 gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
              statusFilter === 'all'
                ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500/20 shadow-2xs'
                : 'bg-white border-slate-200 hover:bg-slate-100'
            }`}
          >
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-tight">Total Jobs</div>
            <div className="text-lg font-bold text-slate-900 mt-0.5">{overallMetrics.total}</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('on_time')}
            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
              statusFilter === 'on_time'
                ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-500/20 shadow-2xs'
                : 'bg-white border-slate-200 hover:bg-slate-100'
            }`}
          >
            <div className="text-[11px] font-bold text-emerald-700 flex items-center space-x-1 uppercase tracking-tight">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span>On Time</span>
            </div>
            <div className="text-lg font-bold text-emerald-900 mt-0.5">{overallMetrics.onTime}</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('due_today')}
            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
              statusFilter === 'due_today'
                ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-500/20 shadow-2xs'
                : 'bg-white border-slate-200 hover:bg-slate-100'
            }`}
          >
            <div className="text-[11px] font-bold text-amber-700 flex items-center space-x-1 uppercase tracking-tight">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              <span>Due Today</span>
            </div>
            <div className="text-lg font-bold text-amber-900 mt-0.5">{overallMetrics.dueToday}</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('overdue')}
            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
              statusFilter === 'overdue'
                ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-500/20 shadow-2xs'
                : 'bg-white border-slate-200 hover:bg-slate-100'
            }`}
          >
            <div className="text-[11px] font-bold text-rose-700 flex items-center space-x-1 uppercase tracking-tight">
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
              <span>Overdue</span>
            </div>
            <div className="text-lg font-bold text-rose-900 mt-0.5">{overallMetrics.overdue}</div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('completed')}
            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
              statusFilter === 'completed'
                ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-500/20 shadow-2xs'
                : 'bg-white border-slate-200 hover:bg-slate-100'
            }`}
          >
            <div className="text-[11px] font-bold text-indigo-700 flex items-center space-x-1 uppercase tracking-tight">
              <CheckCircle2 className="w-2.5 h-2.5" />
              <span>Completed</span>
            </div>
            <div className="text-lg font-bold text-indigo-900 mt-0.5">{overallMetrics.completed}</div>
          </button>
        </div>

        {/* Filter Controls Row */}
        <div className="px-6 py-2.5 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center space-x-2 flex-1 max-w-sm">
            <div className="relative w-full">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search job code, party, role..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-xs font-semibold text-slate-600">Filter Team Member:</label>
            <select
              value={selectedMember}
              onChange={e => setSelectedMember(e.target.value)}
              className="px-2.5 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-2xs"
            >
              <option value="all">All Team Members ({TEAM_MEMBERS.length})</option>
              {TEAM_MEMBERS.map(tm => (
                <option key={tm.name} value={tm.name}>
                  {tm.name} ({tm.role})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Body Content: Team Members and their Jobs List */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 bg-slate-50/50">
          {memberData
            .filter(data => selectedMember === 'all' || data.member.name.toLowerCase() === selectedMember.toLowerCase())
            .map(data => {
              // Filter jobs for this member based on status and search query
              const filteredTasks = data.taskList.filter(item => {
                // Status filter
                if (statusFilter === 'on_time' && item.taskInfo.status !== 'on_time') return false;
                if (statusFilter === 'due_today' && item.taskInfo.status !== 'due_today') return false;
                if (statusFilter === 'overdue' && item.taskInfo.status !== 'overdue') return false;
                if (statusFilter === 'completed' && item.taskInfo.status !== 'completed') return false;

                // Search query
                if (searchQuery.trim()) {
                  const q = searchQuery.toLowerCase();
                  const code = item.project.dveplCode.toLowerCase();
                  const party = (item.project.partyName || item.project.companyName || '').toLowerCase();
                  const role = item.assignedRoles.toLowerCase();
                  return code.includes(q) || party.includes(q) || role.includes(q);
                }
                return true;
              });

              if (filteredTasks.length === 0 && (statusFilter !== 'all' || searchQuery.trim())) {
                return null;
              }

              return (
                <div
                  key={data.member.name}
                  className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden"
                >
                  {/* Member Header Card */}
                  <div className="px-5 py-3.5 bg-slate-100/70 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center text-sm shadow-2xs">
                        {data.member.name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-bold text-slate-900 text-sm">{data.member.name}</h3>
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                            {data.member.role}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">{data.member.email} • {data.member.phone}</p>
                      </div>
                    </div>

                    {/* Member Stats Badges & Quick Action */}
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-bold text-slate-700 bg-white px-2 py-1 rounded-lg border border-slate-200 shadow-2xs">
                        {data.total} Total Assigned
                      </span>
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-200">
                        {data.onTimeCount} On Time
                      </span>
                      {data.dueTodayCount > 0 && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200">
                          {data.dueTodayCount} Due Today
                        </span>
                      )}
                      <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2 py-1 rounded-lg border border-rose-200">
                        {data.overdueCount} Overdue
                      </span>
                      <span className="text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded-lg border border-indigo-200">
                        {data.completedCount} Done
                      </span>

                      {onFilterEngineerInTable && (
                        <button
                          type="button"
                          onClick={() => {
                            onFilterEngineerInTable(data.member.name);
                            onClose();
                          }}
                          className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg border border-blue-200 transition-colors ml-2 cursor-pointer flex items-center space-x-1"
                        >
                          <span>Filter Table</span>
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Member's Assigned Jobs Table */}
                  {filteredTasks.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-400">
                      No jobs match the selected status filter for {data.member.name}.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 font-semibold text-[11px] uppercase tracking-tight">
                            <th className="py-2 px-4">Job Code & S.No</th>
                            <th className="py-2 px-4">Customer / Party Name</th>
                            <th className="py-2 px-4">Role in Project</th>
                            <th className="py-2 px-4">Target Date to Send Drawings</th>
                            <th className="py-2 px-4">Task Status</th>
                            <th className="py-2 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredTasks.map(({ project: proj, taskInfo, assignedRoles }) => {
                            const partyName = proj.partyName || proj.companyName || 'Unknown Customer';
                            const targetDateDisplay = formatToDDMMYY(proj.drawingTargetDate || proj.drawingSendDate) || 'Not Set';

                            return (
                              <tr key={proj.id} className="hover:bg-blue-50/30 transition-colors">
                                <td className="py-2.5 px-4 font-mono font-bold text-slate-900">
                                  <div className="flex items-center space-x-1.5">
                                    <span className="text-blue-600">{proj.dveplCode}</span>
                                    {proj.sNo && proj.sNo !== proj.dveplCode && (
                                      <span className="text-slate-400 text-[10px]">({proj.sNo})</span>
                                    )}
                                  </div>
                                </td>

                                <td className="py-2.5 px-4 font-medium text-slate-800">
                                  <div className="flex items-center space-x-1">
                                    <Building className="w-3 h-3 text-slate-400 shrink-0" />
                                    <span className="truncate max-w-xs">{partyName}</span>
                                  </div>
                                </td>

                                <td className="py-2.5 px-4 text-slate-600">
                                  <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium text-[11px]">
                                    {assignedRoles}
                                  </span>
                                </td>

                                <td className="py-2.5 px-4">
                                  <div className="flex items-center space-x-1 text-slate-700 font-medium">
                                    <Calendar className="w-3 h-3 text-slate-400" />
                                    <span>{targetDateDisplay}</span>
                                  </div>
                                </td>

                                <td className="py-2.5 px-4">
                                  <span
                                    className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-bold border ${
                                      taskInfo.color === 'emerald'
                                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                        : taskInfo.color === 'amber'
                                        ? 'bg-amber-50 text-amber-800 border-amber-300'
                                        : taskInfo.color === 'rose'
                                        ? 'bg-rose-50 text-rose-800 border-rose-300'
                                        : 'bg-indigo-50 text-indigo-800 border-indigo-300'
                                    }`}
                                  >
                                    {taskInfo.status === 'completed' && <CheckCircle className="w-3 h-3 text-indigo-600" />}
                                    {taskInfo.status === 'on_time' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                                    {taskInfo.status === 'due_today' && <Clock className="w-3 h-3 text-amber-600" />}
                                    {taskInfo.status === 'overdue' && <AlertTriangle className="w-3 h-3 text-rose-600" />}
                                    <span>{taskInfo.label}</span>
                                  </span>
                                </td>

                                <td className="py-2.5 px-4 text-right">
                                  {onSelectProject && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        onSelectProject(proj);
                                        onClose();
                                      }}
                                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                    >
                                      View Details
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <div className="text-xs text-slate-500">
            Target dates determine team drawing delivery commitments. Updating target dates synchronizes status across all views.
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer shadow-2xs"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
